import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  CreateOrganizerRequest, CyclingEvent, EventEdition, OrganizerVisibility, PublicOrganizer,
  displayBundesland, toExternalUrl,
} from '@radevents/shared';
import OrganizerOutreachStatus from '../components/outreach/OrganizerOutreachStatus';
import { useAuth } from '../contexts/AuthContext';
import PublicHeader from '../components/Layout/PublicHeader';
import OrganizerSocialLinks from '../components/OrganizerSocialLinks';
import OrganizerMasthead from '../components/organizer/OrganizerMasthead';
import OrganizerLetterhead, { LetterheadRow } from '../components/organizer/OrganizerLetterhead';
import PremiumStatus from '../components/organizer/PremiumStatus';
import OrganizerManage from '../components/organizer/OrganizerManage';
import PremiumDialog from '../components/organizer/PremiumDialog';
import { billingDefaults } from '../components/organizer/BillingFields';
import AssetUploadPreview from '../components/organizer/AssetUploadPreview';
import { ORGANIZER_SPACE_CSS, BERRY, INK, MUTED, PAGE_BG } from '../components/organizer/organizerSpace';
import { CLAIM_INTENT_KEY, CLAIM_TOKEN_KEY, claimTokenPath } from '../components/organizer/claimIntent';
import OrganizerAccessDialog from '../components/organizer/OrganizerAccessDialog';
import api from '../services/api';
import eventService from '../services/eventService';
import OrganizerSeason from '../components/organizer/OrganizerSeason';
import OrganizerKontakt from '../components/organizer/OrganizerKontakt';
import AppTopbarBrand from '../components/Layout/AppTopbarBrand';
import { useEventMerken } from '../hooks/useEventMerken';
import OrganizerChallengeBlock from '../components/winterchallenge/OrganizerChallengeBlock';
import organizerAccessService from '../services/organizerAccessService';
import organizerPortalService from '../services/organizerPortalService';
import { useOrganizerAssets } from '../components/organizer/useOrganizerAssets';
import { organizerSeo } from '../components/organizer/organizerSeo';
import OrganizerProposeDialog from '../components/organizer/OrganizerProposeDialog';
import { useOrganizerPortal } from '../contexts/OrganizerPortalContext';
import { trackOrganizerFunnel } from '../services/trackingService';

/**
 * Adresse auf der Karte statt zum Abtippen.
 *
 * Bewusst die Suche und keine Koordinaten: der Veranstalter hat eine Anschrift hinterlegt, keine
 * Geodaten — eine Suchanfrage trifft die Vereinsheim-Adresse zuverlässiger als ein Pin, den wir
 * aus unvollständigen Angaben selbst berechnen. `?api=1` ist die dokumentierte, stabile Form und
 * funktioniert auch dort, wo die App statt der Website öffnet.
 */
const mapsUrl = (parts: (string | undefined)[]): string | undefined => {
  const query = parts.filter(Boolean).join(', ').trim();
  if (!query) return undefined;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
};

/**
 * Die Veranstalterseite — für Besucher und für den Verein selbst.
 *
 * **Eine Seite, zwei Zustände.** Vorher gab es eine öffentliche Seite und daneben einen
 * getrennten Bereich zum Pflegen. Das hieß zwei Gestaltungen, zwei Wahrheiten und einen Knopf
 * „Diesen Veranstalter verwalten", der wegführte statt zu helfen. Jetzt sieht der Verein genau
 * das, was Besucher sehen, und kann es an derselben Stelle ändern: leere Plätze für Logo und
 * Banner werden ansprechbar, hinter jeder Angabe steht, wer sie sieht, und darunter erscheinen
 * die Reiter für Events, Sichtbarkeit, Zahlen und Einreichungen ([OrganizerManage]).
 *
 * Für Suchmaschinen ändert sich nichts: alles Zusätzliche hängt an der Anmeldung, ein Bot sieht
 * weiterhin die reine öffentliche Seite samt strukturierten Daten.
 */
export default function OrganizerDetail() {
  const { id } = useParams<{ id: string }>();
  const organizerId = Number(id);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedOffer = searchParams.get('angebot');
  const offerIntent = requestedOffer === 'premium' || requestedOffer === 'promotion'
    ? requestedOffer : undefined;
  const { isAdmin, isAdminOrModerator, isAuthenticated } = useAuth();

  // Pflegt das angemeldete Konto genau diesen Veranstalter?
  const { organizers: myOrganizers, reload: reloadPortal } = useOrganizerPortal();
  const summary = myOrganizers.find(o => o.id === organizerId);
  const managesThis = !!summary;
  const admin = isAdmin();
  const redaktion = isAdminOrModerator();

  const [organizer, setOrganizer] = useState<PublicOrganizer | null>(null);
  const [events, setEvents] = useState<CyclingEvent[]>([]);
  const [editions, setEditions] = useState<EventEdition[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);

  /**
   * Stammdaten, Logo und Banner der eigenen Seite — Zustand, Nachladen und die beiden Handler
   * liegen zusammen in `useOrganizerAssets`. Sie teilen sich `profile`, und jeder von ihnen
   * schreibt es; verteilt über die Seite war das nicht mehr zu übersehen.
   */
  const {
    profile, loadProfile, uploadingAsset, pickedAsset, setPickedAsset, pendingUrls,
    uploadAsset, removeAsset,
  } = useOrganizerAssets({
    organizerId,
    managesThis,
    onPublicChanged: () => loadPublic(),
    // Ein 402 ist kein Fehler, sondern das Angebot — der Server ist die Grenze, dieser Dialog
    // die Erklärung.
    onPremiumRequired: () => openPremium(false),
  });


  /**
   * Der Premium-Dialog. Er wird **hier** gehalten und nicht im Pflege-Teil: alle Haken —
   * Banner, Balken, Termin, Reiter *Statistiken*, Reiter *Sichtbarkeit* — öffnen denselben
   * Dialog, und zwei Mount-Punkte hießen zwei Zustände derselben Erklärung.
   */
  const [showPremium, setShowPremium] = useState(false);
  /**
   * Der Dialog ohne Verkaufsteil.
   *
   * Die Verkaufskarte im Reiter *Sichtbarkeit & Promotion* nennt Nutzen, Beleg und Preis bereits; wer
   * dort „Anfrage senden" drückt, hat entschieden. Ihm dieselben vier Punkte noch einmal
   * vorzulegen hält ihn auf. Die Haken an gesperrten Stellen öffnen weiter den vollen Dialog.
   */
  const [premiumOrdering, setPremiumOrdering] = useState(false);
  const openPremium = (asOrder: boolean) => { setPremiumOrdering(asOrder); setShowPremium(true); };

  // Der auf der Angebotsseite gewählte Weg endet nicht an der Veranstalterliste: nach einem
  // vorhandenen Zugang öffnet Premium direkt den Checkout, die Hervorhebung den passenden Reiter.
  useEffect(() => {
    if (!managesThis || !offerIntent) return;
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (offerIntent === 'premium') {
      openPremium(true);
      next.delete('angebot');
      changed = true;
    }
    if (next.has('antrag')) {
      next.delete('antrag');
      changed = true;
    }
    // Die Hervorhebung bleibt in der URL, bis ein Event gewählt wurde. So bleibt der Kontext
    // auch beim Neuladen erhalten und der passende Abschnitt steht weiterhin zuerst.
    if (changed) setSearchParams(next, { replace: true });
  // Nur beim Übergang in den Pflegezustand ausführen; openPremium ist absichtlich kein Hook.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managesThis, offerIntent]);
  /**
   * Es liegt eine Premium-Anfrage bei uns.
   *
   * Steuert nur die Wortwahl im Pflege-Balken („Premium angefragt" statt „Basis-Zugang") und
   * nimmt den Knopf weg — eine zweite Anfrage für dieselbe Sache hilft niemandem.
   */
  const [premiumRequested, setPremiumRequested] = useState(false);
  /** Zahlungsziel der vorläufig freigeschalteten Premium-Buchung — siehe PremiumStatus. */
  /** Dasselbe für den Stammdaten-Dialog: der Knopf steht oben, das Formular gehört nach unten. */
  const [savingProfile, setSavingProfile] = useState(false);
  /** Nach einem gespeicherten Vorschlag: der Reiter *Eingereicht* soll ihn zeigen. */
  const [profileSavedSignal, setProfileSavedSignal] = useState(0);
  const [showPropose, setShowPropose] = useState(false);
  /**
   * „Ich bin der Veranstalter" öffnet denselben Dialog wie die Angebotsseite
   * ([OrganizerAccessDialog]), nur mit gesetztem Veranstalter. Hier stand bis zum 22.09.2026 eine
   * zweite Fassung mit Freitextfeld — zwei Dialoge für dieselbe Sache, und der Nachweis lief an
   * zwei Stellen auseinander.
   */
  const [showClaim, setShowClaim] = useState(false);
  const [claimed, setClaimed] = useState(false);

  usePageTitle(organizer?.name ?? 'Veranstalter');


  const loadPublic = useCallback(() => {
    if (!organizerId) return;
    api.get(`/organizers/${organizerId}`)
      .then(r => {
        setOrganizer(r.data);
        setLoadingEvents(true);
        // `includePast` (letztes Argument der Reihe) ist gesetzt: der Haken unter der Liste
        // schaltet nur die Anzeige um und laedt nicht nach. Ein Verein mit langer Geschichte
        // waere sonst bei jedem Klick eine Wartezeit.
        return eventService.getAll(
          0, 200, undefined, undefined, undefined, undefined, undefined, undefined, undefined,
          undefined, undefined, organizerId, undefined, undefined, undefined, undefined,
          undefined, undefined, undefined, true
        );
      })
      .then(r => setEvents(r.content))
      .catch(() => {})
      .finally(() => { setLoading(false); setLoadingEvents(false); });
    // Frühere Auflagen getrennt und ohne Wartezeit für die Seite: fehlen sie, fehlt nur die
    // Vergangenheit, nicht die Veranstalterseite.
    eventService.getOrganizerEditions(organizerId).then(setEditions).catch(() => setEditions([]));
  }, [organizerId]);

  useEffect(() => { window.scrollTo(0, 0); loadPublic(); }, [loadPublic]);

  const merken = useEventMerken(useCallback((eventId: number, favorited: boolean) => {
    setEvents(prev => prev.map(e => e.id === eventId ? { ...e, favorited } : e));
  }, []));
  const goBack = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate('/veranstalter');
  };



  // Kein Umweg über die Anmeldung mehr: der Dialog erledigt sie selbst, mit Code an die
  // E-Mail-Adresse. Vorher führte der Weg über /login und die Absicht musste über den Speicher
  // gerettet werden.
  const handleClaimClick = useCallback((eingang?: 'kopfzeile') => {
    trackOrganizerFunnel('claim_started', { offer: offerIntent, organizerId, entry: eingang });
    setShowClaim(true);
  }, [organizerId, offerIntent]);

  /**
   * Der Einmal-Link aus der Outreach-Mail (`?freischalten=`).
   *
   * Drei Fälle: nicht angemeldet, dann führt der Weg über die Registrierung und der Token liegt
   * solange im Speicher. Angemeldet, dann wird er sofort eingelöst. Schon Verwalter, dann ist
   * nichts zu tun, aber der Token wird trotzdem verbraucht, damit er nicht im Postfach liegen
   * bleibt.
   *
   * Der Parameter wird in **jedem** Fall aus der Adresse genommen: er ist ein Geheimnis und hat
   * in Verlauf, Lesezeichen und Referrer nichts verloren.
   */
  useEffect(() => {
    if (!organizerId) return;
    let token = searchParams.get('freischalten');
    if (!token) {
      try { token = localStorage.getItem(CLAIM_TOKEN_KEY); } catch { /* Privatmodus */ }
      if (!token || !isAuthenticated) return;
    }
    if (searchParams.get('freischalten')) {
      const next = new URLSearchParams(searchParams);
      next.delete('freischalten');
      setSearchParams(next, { replace: true });
    }
    if (!isAuthenticated) {
      navigate(claimTokenPath(organizerId, token));
      return;
    }
    const value = token;
    try { localStorage.removeItem(CLAIM_TOKEN_KEY); } catch { /* Privatmodus */ }
    organizerAccessService.claim(value)
      .then(() => {
        toast.success('Ihr seid jetzt als Veranstalter verbunden.');
        loadPublic();
      })
      .catch(() => toast.error('Dieser Link ist abgelaufen oder wurde schon benutzt. Stell den Antrag hier auf der Seite.'));
  }, [organizerId, isAuthenticated, searchParams, setSearchParams, navigate, loadPublic]);

  // Zurück von der Anmeldung: den Antrag öffnen, für den jemand sich gerade angemeldet hat.
  useEffect(() => {
    if (!isAuthenticated || managesThis || claimed) return;
    let stored: string | null = null;
    try { stored = localStorage.getItem(CLAIM_INTENT_KEY); } catch { /* Privatmodus */ }
    const wanted = searchParams.get('antrag') === '1' || stored === String(organizerId);
    if (!wanted) return;
    try { localStorage.removeItem(CLAIM_INTENT_KEY); } catch { /* Privatmodus */ }
    if (searchParams.get('antrag')) {
      const next = new URLSearchParams(searchParams);
      next.delete('antrag');
      setSearchParams(next, { replace: true });
    }
    setShowClaim(true);
  }, [isAuthenticated, managesThis, claimed, organizerId, searchParams, setSearchParams]);

  /**
   * Kommende und vergangene Events getrennt — beide kommen aus derselben Abfrage.
   *
   * Vergangene stehen hinter einem Haken, weil sie die Antwort auf eine andere Frage sind: wer
   * die Seite aufruft, will wissen, wo er mitfahren kann. Sie ganz wegzulassen war aber falsch:
   * ein Verein ohne aktuellen Termin sah aus, als hätte er nie einen gehabt — und die
   * Outreach-Mail verwies auf eine Seite, die dann leer war.
   */
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const [upcoming, past] = useMemo(() => {
    // Jederzeit fahrbar ist nie vergangen (Platzhalter 1970) — und steht hinter den Terminen.
    const isPast = (ev: CyclingEvent) =>
      !ev.permanent && new Date(`${ev.endDate ?? ev.eventDate}T00:00:00`).getTime() < today.getTime();
    const ahead = events.filter(ev => !isPast(ev));
    return [
      [...ahead.filter(ev => !ev.permanent), ...ahead.filter(ev => ev.permanent)],
      // Aufsteigend wie die kommenden: beide Listen bilden zusammen **eine** Zeitachse, oben das
      // älteste. Jüngstes-zuerst las sich von oben nach unten als Sprung vorwärts und dann
      // rückwärts — die Linie stimmte, die Reihenfolge nicht.
      events.filter(isPast).sort((a, b) => a.eventDate.localeCompare(b.eventDate)),
    ];
  }, [events, today]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: PAGE_BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#6A6A6A', fontSize: 14 }}>Laden…</div>
      </div>
    );
  }

  if (!organizer) {
    return (
      <div style={{ minHeight: '100vh', background: PAGE_BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#6A6A6A', fontSize: 14 }}>Veranstalter nicht gefunden.</div>
      </div>
    );
  }

  const premium = !!(profile?.verification?.active ?? summary?.premium);
  const kopfZeile = [
    loadingEvents ? null : upcoming.length === 1 ? '1 kommendes Event' : `${upcoming.length} kommende Events`,
    organizer.city,
    organizer.bundesland ? displayBundesland(organizer.bundesland) : null,
  ].filter(Boolean).join(' · ');
  const {
    canonicalUrl, pageTitle, pageDescription, orgSchema, breadcrumbSchema, eventListSchema,
  } = organizerSeo(organizer, events);

  /**
   * Der Briefkopf.
   *
   * Besucher sehen, was öffentlich steht — das entscheidet der Server (PublicOrganizerResponse).
   * Der Verein selbst sieht **alle** Angaben samt Marke, wer sie sieht; nur so ist die Frage
   * „steht meine Telefonnummer im Netz?" überhaupt zu beantworten.
   */
  const letterheadRows: LetterheadRow[] = managesThis && profile ? [
    {
      label: 'Ansprechpartner', value: profile.contactPerson,
      field: 'publicContactPerson', isPublic: profile.visibility?.publicContactPerson,
    },
    {
      label: 'E-Mail', value: profile.email, href: profile.email ? `mailto:${profile.email}` : undefined,
      hint: 'E-Mail schreiben',
      field: 'publicEmail', isPublic: profile.visibility?.publicEmail,
    },
    {
      label: 'Telefon', value: profile.phone, href: profile.phone ? `tel:${profile.phone.replace(/[^+\d]/g, '')}` : undefined,
      hint: 'Anrufen',
      field: 'publicPhone', isPublic: profile.visibility?.publicPhone,
    },
    {
      label: 'Website', value: profile.website?.replace(/^https?:\/\//, '').replace(/\/$/, ''),
      href: profile.website ? toExternalUrl(profile.website) : undefined, external: true,
      hint: 'Website in neuem Tab öffnen',
      field: 'publicWebsite', isPublic: profile.visibility?.publicWebsite,
    },
    {
      label: 'Anschrift',
      value: [profile.street, [profile.postalCode, profile.city].filter(Boolean).join(' ')].filter(Boolean).join(', '),
      href: mapsUrl([profile.street, profile.postalCode, profile.city]), external: true,
      hint: 'Auf Google Maps ansehen',
      field: 'publicAddress',
      isPublic: profile.visibility?.publicAddress,
    },
    { label: 'Region', value: profile.bundesland ? displayBundesland(profile.bundesland) : '' },
  ] : [
    { label: 'Ansprechpartner', value: organizer.contactPerson },
    {
      label: 'E-Mail', value: organizer.email,
      href: organizer.email ? `mailto:${organizer.email}` : undefined, hint: 'E-Mail schreiben',
    },
    {
      label: 'Telefon', value: organizer.phone,
      href: organizer.phone ? `tel:${organizer.phone.replace(/[^+\d]/g, '')}` : undefined, hint: 'Anrufen',
    },
    {
      label: 'Website', value: organizer.website?.replace(/^https?:\/\//, '').replace(/\/$/, ''),
      href: organizer.website ? toExternalUrl(organizer.website) : undefined, external: true,
      hint: 'Website in neuem Tab öffnen',
    },
    {
      label: 'Anschrift',
      value: [organizer.street, [organizer.postalCode, organizer.city].filter(Boolean).join(' ')].filter(Boolean).join(', '),
      href: mapsUrl([organizer.street, organizer.postalCode, organizer.city]), external: true,
      hint: 'Auf Google Maps ansehen',
    },
    { label: 'Region', value: organizer.bundesland ? displayBundesland(organizer.bundesland) : '' },
  ];

  /**
   * Speichert den Briefkopf: **beides in einem Zug**, ausgelöst von einem Knopf.
   *
   * Die Sichtbarkeit zuerst — sie wirkt sofort und ohne Prüfung, die Angaben gehen durch die
   * Warteschlange. Andersherum stünde nach einem Fehlschlag ein eingereichter Vorschlag neben
   * einer Telefonnummer, die entgegen der Anzeige weiter im Netz steht; von beiden Reihenfolgen
   * die schlechtere.
   *
   * Die Rückmeldung sagt die Wahrheit je nach `autoApproveEdits` — ein „ist online", das nicht
   * stimmt, merkt der Verein spätestens beim Nachsehen.
   */
  const saveProfile = async (payload: CreateOrganizerRequest, visibility: OrganizerVisibility) => {
    setSavingProfile(true);
    try {
      const before = profile?.visibility;
      const visDirty = !before
        || (Object.keys(visibility) as (keyof OrganizerVisibility)[]).some(k => visibility[k] !== before[k]);
      if (visDirty) await organizerPortalService.updateVisibility(organizerId, visibility);
      await organizerPortalService.proposeProfileEdit(organizerId, {
        proposedName: payload.name,
        proposedWebsite: payload.website,
        proposedLogoUrl: payload.logoUrl,
        proposedStreet: payload.street,
        proposedPostalCode: payload.postalCode,
        proposedCity: payload.city,
        proposedBundesland: payload.bundesland,
        proposedImpressumUrl: payload.impressumUrl,
        proposedContactPerson: payload.contactPerson,
        proposedEmail: payload.email,
        proposedPhone: payload.phone,
        proposedStravaClubUrl: payload.stravaClubUrl,
        proposedInstagramUrl: payload.instagramUrl,
        proposedFacebookUrl: payload.facebookUrl,
        proposedKomootUrl: payload.komootUrl,
      });
      toast.success(summary?.autoApproveEdits
        ? 'Gespeichert. Die Änderung ist online.'
        : 'Gespeichert. Sie erscheint auf der Seite, sobald wir sie kurz geprüft haben.');
      loadProfile();
      loadPublic();
      reloadPortal();
      setProfileSavedSignal(n => n + 1);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Konnte nicht gespeichert werden');
      throw err;
    } finally {
      setSavingProfile(false);
    }
  };


  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: PAGE_BG, display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        {(organizer.photoUrl || organizer.logoUrl) && (
          <meta property="og:image" content={organizer.photoUrl || organizer.logoUrl} />
        )}
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {eventListSchema && <script type="application/ld+json">{JSON.stringify(eventListSchema)}</script>}
      </Helmet>
      <style>{ORGANIZER_SPACE_CSS}</style>
      <div className="hide-mobile"><PublicHeader /></div>
      <div className="app-topbar hide-desktop"><AppTopbarBrand onBack={goBack} /></div>

      <div className={`app-page org-seite${managesThis ? '' : ' org-seite--breit'}`}>
        {/* Die Krume ist der Weg ins Verzeichnis — und der einzige. Ein zweiter, ausgeschriebener
            Link daneben („Zu allen Veranstaltern") war ein Anlauf und ist wieder raus: zwei Wege
            zum selben Ziel in einer Zeile sind einer zu viel. Damit man den einen auch sieht,
            steht „‹ VERANSTALTER" in der Akzentfarbe; grau las es sich wie eine Pfadangabe.
            Ein echter Link statt eines `div` mit `onClick`: das war mit der Tastatur nicht
            erreichbar und ließ sich nicht in einem neuen Tab öffnen. Verlinkt ist nur der erste
            Teil; der Name ist die Seite, auf der man steht. */}
        <p className="vb-crumb hide-mobile">
          <Link to={`/veranstalter${offerIntent ? `?angebot=${offerIntent}` : ''}`}>‹ VERANSTALTER</Link>
          <span className="vb-crumb-here"> / {organizer.name}</span>
        </p>

        <OrganizerMasthead
          name={organizer.name}
          logoUrl={managesThis ? (profile?.logoUrl ?? organizer.logoUrl) : organizer.logoUrl}
          bannerUrl={managesThis ? (profile?.photoUrl ?? organizer.photoUrl) : organizer.photoUrl}
          verified={organizer.verified}
          manage={managesThis}
          premium={premium}
          uploading={uploadingAsset}
          logoUploaded={profile?.logoUploaded}
          bannerUploaded={profile?.bannerUploaded}
          logoPendingUrl={pendingUrls.logo}
          bannerPendingUrl={pendingUrls.banner}
          onPick={(kind, file) => setPickedAsset({ kind, file })}
          onRemove={removeAsset}
          onPremiumHint={() => openPremium(false)}
          meta={kopfZeile}
        />

        {/* „Das ist euer Verein?“ gleich unter dem Kopf (28.10.2026). Der Abschnitt „Bist du der
            Veranstalter?“ steht unter allen Events; die Seiten hatten in 90 Tagen 8.352 Sitzungen,
            angefangen haben 92. Dieselbe Bedingung wie unten, gemessen als Eingang `kopfzeile`. */}
        {!redaktion && !managesThis && !organizer.verified && !offerIntent && !claimed && (
          <div className="vb-uebernehmen">
            <span>Das ist euer Verein?</span>
            <button type="button" onClick={() => handleClaimClick('kopfzeile')}>Seite kostenlos übernehmen</button>
          </div>
        )}

        {/* Der Kaufwunsch bleibt nach der Veranstalterauswahl sichtbar. Ohne diesen Übergang
            sah die öffentliche Profilseite wie ein Abbruch des zuvor gewählten Weges aus. */}
        {!managesThis && offerIntent && (
          <section style={{ margin: '0 0 24px', padding: '20px 22px', borderRadius: 16,
            background: 'rgba(146,44,87,.07)', border: '1px solid rgba(146,44,87,.18)' }}>
            <div className="vb-eyebrow">
              {offerIntent === 'promotion' ? 'Event hervorheben' : 'Premium aktivieren'}
            </div>
            <h2 style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 21,
              color: INK, margin: '6px 0 8px', letterSpacing: '-.01em' }}>
              {offerIntent === 'promotion'
                ? `Event von ${organizer.name} hervorheben`
                : `Premium für ${organizer.name}`}
            </h2>
            {organizer.verified ? (
              <>
                <p style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.55, margin: '0 0 14px', maxWidth: 650 }}>
                  Dieser Veranstalter wird bereits über ein anderes Konto verwaltet. Melde dich
                  mit diesem Konto an oder schreib uns, wenn du ebenfalls Zugriff benötigst.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {!isAuthenticated && (
                    <Link className="vb-btn vb-btn--primary"
                      to={`/login?redirect=${encodeURIComponent(`/veranstalter/${organizerId}?angebot=${offerIntent}`)}`}>
                      Anmelden
                    </Link>
                  )}
                  <Link className="vb-btn" to="/kontakt">Fragen? Schreib uns</Link>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.55, margin: '0 0 14px', maxWidth: 650 }}>
                  Um fortzufahren, bestätige zuerst, dass du diesen Veranstalter verwaltest.
                  Mit einer passenden, bestätigten E-Mail-Adresse erhältst du sofort Zugang.
                </p>
                <button className="vb-btn vb-btn--primary" onClick={() => handleClaimClick()} disabled={claimed}>
                  {claimed
                    ? 'Antrag eingegangen — wir melden uns'
                    : offerIntent === 'promotion'
                      ? 'Zugang erhalten und Event auswählen'
                      : 'Zugang erhalten und Premium aktivieren'}
                </button>
              </>
            )}
          </section>
        )}

        {/* Der Balken sagt nicht nur, dass man hier verwaltet — er trägt auch die Aktion dazu.
            „Stammdaten bearbeiten" stand vorher allein zwischen Briefkopf und Reitern und sah
            aus, als sei es dort liegengeblieben. Hier steht es bei seinesgleichen: alles in
            diesem Balken sieht nur, wer den Verein verwaltet. */}
        {managesThis && (
          <div className="om-hint">
            <strong style={{ color: BERRY }}>Du verwaltest diesen Veranstalter.</strong>
            {/* Läuft Premium, sagt die Pille die Restlaufzeit — sonst steht hier, was gerade
                gilt, und daneben der Weg dorthin. Der Balken ist die einzige Stelle, die
                unabhängig vom gewählten Reiter im Blick bleibt. */}
            {premium ? (
              <PremiumStatus
                verification={profile?.verification}
                variant="bar"
                requested={premiumRequested}
                onExtend={() => openPremium(false)}
              />
            ) : (
              <>
                <span className="om-plan">
                  {premiumRequested ? 'Zahlung ausstehend' : 'Kostenlos'}
                </span>
                {!premiumRequested && (
                  <button type="button" className="om-unlock" onClick={() => openPremium(false)}>
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden>
                      <path d="M12 2.6l2.7 5.9 6.4.7-4.8 4.3 1.3 6.3L12 16.6l-5.6 3.2 1.3-6.3L2.9 9.2l6.4-.7z" />
                    </svg>
                    Premium freischalten
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Der Briefkopf steht immer — für Besucher wie für den Verein selbst.
            Er war im Pflege-Zustand zugeklappt, um die Terminliste höher beginnen zu lassen; das
            war den Preis nicht wert. Die Stammdaten sind die Angaben, die auf der Seite stehen
            und über deren Sichtbarkeit der Verein entscheidet — sie hinter einen Knopf zu legen
            heißt, dass niemand merkt, wenn dort etwas Falsches oder Fehlendes steht. „Bearbeiten"
            steht bei den Angaben selbst und nicht im Balken darüber. */}
        {managesThis && summary ? (
          <>
          <OrganizerLetterhead
            rows={letterheadRows}
            manage
            edit={profile ? { profile, saving: savingProfile, onSubmit: saveProfile } : undefined}
          />
          <OrganizerSocialLinks organizer={organizer} style={{ margin: '0 0 26px' }} />
          {/* Winterchallenge (docs/bereiche/winterchallenge.md): wer den Verein verwaltet, sieht
              ohne Team die Einladung. */}
          <OrganizerChallengeBlock organizerId={organizerId} organizerName={organizer.name} />
          <OrganizerManage
            organizerId={organizerId}
            summary={summary}
            profile={profile}
            onProfileChanged={() => { loadProfile(); loadPublic(); reloadPortal(); }}
            profileSavedSignal={profileSavedSignal}
            premiumRequested={premiumRequested}
            pendingAssets={{ logo: !!pendingUrls.logo, banner: !!pendingUrls.banner }}
            onPremiumRequestedChange={setPremiumRequested}
            onPremiumHint={() => openPremium(false)}
            onPremiumOrder={() => openPremium(true)}
            initialTab={offerIntent === 'promotion' ? 'sichtbarkeit' : undefined}
            promotionIntent={offerIntent === 'promotion'}
          />
          </>
        ) : (
          /* Besucher (30.10.2026): links die Events als Zeilen wie auf der Startseite, rechts
             Kontakt, Team und „Bist du der Veranstalter?". Auf dem Handy steht der Kontakt als
             Knopfreihe über den Events (Reihenfolge in App.css, `.org-raster`). */
          <div className="org-raster">
            <aside className="org-spalte">
            <OrganizerKontakt rows={letterheadRows} organizer={organizer} />
            </aside>
            <div className="org-haupt">
            <OrganizerSeason upcoming={upcoming} past={past} editions={editions} loading={loadingEvents}
              onFavorite={ev => void merken(ev)} />
            </div>
            <div className="org-spalte org-spalte--unten">
            {/* Winterchallenge: das Team des Vereins, für Besucher nur wenn es eins gibt. */}
            <OrganizerChallengeBlock organizerId={organizerId} organizerName={organizer.name} />

            {/* Der Anreiz. Ohne ihn steht auf der Seite nur ein Knopf „Ich bin der Veranstalter" —
                und niemand drückt einen Knopf, dessen Folgen er nicht kennt.

                Der Zweig gilt ohnehin nur für Besucher (der Verwalter sieht darüber die Reiter),
                `managesThis` steht hier trotzdem: beim kurzen Ausflug in zwei getrennte Seiten
                stand die Sektion einmal unbedingt da, unter „Du verwaltest diesen Veranstalter".

                Und nur, solange den Verein niemand pflegt: `verified` heißt genau das (aktive
                Verknüpfung, siehe `hasActiveMember`). Wo schon jemand die Seite betreut, ist
                „Bist du der Veranstalter?" keine Einladung mehr, sondern die Aufforderung an
                einen Fremden, einen Zugang zu beantragen, den es schon gibt. Ein zweiter Vorstand
                kommt über die Redaktion dazu. */}
            {!redaktion && !managesThis && !organizer.verified && !offerIntent && (
              <section className="org-uebernehmen-karte">
                <div className="vb-eyebrow">Für Veranstalter</div>
                <h2 className="org-uebernehmen-titel">Bist du der Veranstalter?</h2>
                {/* Nur das kostenlose Versprechen. Was Premium kostet und bringt, sieht erst, wer
                    den Veranstalter tatsächlich verwaltet — auf seiner Seite ist eine Preisliste
                    für Besucher Werbung, die sie nichts angeht, und sie stünde ausgerechnet unter
                    den Terminen des Vereins. */}
                <p className="org-uebernehmen-text">
                  Übernimm diese Seite kostenlos: Events selbst eintragen, Logo einstellen,
                  Verifiziert-Haken. Passt deine E-Mail-Adresse zum Verein, geht es sofort.{' '}
                  <Link to="/fuer-veranstalter" style={{ color: BERRY, fontWeight: 600 }}>Was Veranstalter bei uns können</Link>
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  <button className="vb-btn vb-btn--primary" onClick={() => handleClaimClick()} disabled={claimed}>
                    {claimed ? 'Antrag eingegangen — wir melden uns' : 'Ich bin der Veranstalter'}
                  </button>
                  <button className="vb-btn" onClick={() => setShowPropose(true)}>Änderung vorschlagen</button>
                </div>
              </section>
            )}

            {redaktion && <OrganizerOutreachStatus organizerId={organizerId} />}

            {admin && (
              <div style={{ marginTop: 26, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="vb-btn" onClick={() => navigate(`/organizers?edit=${id}`)}>
                  Im Dashboard bearbeiten
                </button>
                <button className="vb-btn" onClick={() => setShowPropose(true)}>Änderung vorschlagen</button>
              </div>
            )}
            </div>
          </div>
        )}
      </div>


      {pickedAsset && (
        <AssetUploadPreview
          kind={pickedAsset.kind}
          file={pickedAsset.file}
          organizerName={organizer.name}
          uploading={uploadingAsset}
          /* Die Datei kommt aus dem Dialog zurück: beim Banner ist es der Zuschnitt, nicht die Vorlage. */
          onConfirm={file => uploadAsset(pickedAsset.kind, file)}
          onCancel={() => setPickedAsset(null)}
          /* „Anderes Bild wählen" öffnet denselben Dateidialog wie der leere Platz — der Weg
             zurück in die Auswahl darf nicht über Abbrechen und Neusuchen führen. */
          onPickOther={() => {
            setPickedAsset(null);
            const input = document.querySelector<HTMLInputElement>(
              `input[type="file"][data-asset="${pickedAsset.kind}"]`,
            );
            input?.click();
          }}
        />
      )}

      {/* Nur für Verwalter. Aufgerufen wird es ausschließlich aus dem Pflege-Zustand heraus —
          vom leeren Banner, vom Zahlen-Reiter, vom Bild-/Flyer-Feld oder von der Bühne. */}
      {managesThis && (
        <PremiumDialog
          show={showPremium}
          organizerId={organizerId}
          organizerName={organizer.name}
          ordering={premiumOrdering}
          billingDefault={billingDefaults(profile, organizer.name)}
          onClose={() => setShowPremium(false)}
          onRequested={() => { setPremiumRequested(true); reloadPortal(); }}
        />
      )}

      {/* Derselbe Dialog wie auf der Angebotsseite, nur mit gesetztem Veranstalter. */}
      <OrganizerAccessDialog
        show={showClaim}
        organizer={organizer}
        offer={offerIntent === 'premium' || offerIntent === 'promotion' || offerIntent === 'free' ? offerIntent : undefined}
        onClose={() => {
          setShowClaim(false);
          setClaimed(true);
          // Der Zugang kann jetzt aktiv sein (passende Adresse): dann zeigt die Seite den
          // Pflegeteil, sobald die Verknüpfungen neu geladen sind.
          reloadPortal();
          loadPublic();
        }}
      />

      {/* Propose Modal */}

      {/* „Änderung vorschlagen" für Besucher — der Weg für alle, die den Verein nicht selbst
          pflegen. Wer ihn pflegt, ändert im Briefkopf. */}
      <OrganizerProposeDialog
        show={showPropose}
        organizer={organizer}
        onClose={() => setShowPropose(false)}
      />
    </div>
  );
}
